import { useState, useRef, useEffect } from 'react';
import { PREDEFINED_SOURCES } from '../utils/googleSheets';
import './PreviewPaneImport.css';

interface PreviewPaneImportProps {
  onImport: (url: string) => Promise<void>;
  onImportMultiple?: (urls: string[]) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  successCounts: {
    spells: number;
    items: number;
    classes: number;
    feats: number;
  } | null;
  warnings: string[];
  onClearStatus: () => void;
  spellsCount: number;
  itemsCount: number;
  classesCount: number;
  featsCount?: number;
  onClearSpells: () => void;
  onClearItems: () => void;
  onClearClasses: () => void;
  onClearFeats?: () => void;
  combinedTemplateUrl: string;
}

export function PreviewPaneImport({
  onImport,
  onImportMultiple,
  isLoading,
  error,
  successCounts,
  warnings,
  onClearStatus,
  spellsCount,
  itemsCount,
  classesCount,
  featsCount = 0,
  onClearSpells,
  onClearItems,
  onClearClasses,
  onClearFeats,
  combinedTemplateUrl,
}: PreviewPaneImportProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [selectedSources, setSelectedSources] = useState<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const toggleSource = (name: string) => {
    setSelectedSources((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const handleImportSelected = async () => {
    if (!onImportMultiple || selectedSources.size === 0) return;
    const urls = PREDEFINED_SOURCES
      .filter((s) => selectedSources.has(s.name))
      .map((s) => s.url);
    await onImportMultiple(urls);
    if (!error) {
      setSelectedSources(new Set());
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (url.trim()) {
      await onImport(url.trim());
      if (!error) {
        setUrl('');
      }
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      setUrl('');
      setSelectedSources(new Set());
      onClearStatus();
    }
    setIsOpen(!isOpen);
  };

  const formatImportSummary = () => {
    if (!successCounts) {
      return null;
    }

    const parts: string[] = [];
    if (successCounts.spells > 0) parts.push(`${successCounts.spells} spell${successCounts.spells === 1 ? '' : 's'}`);
    if (successCounts.items > 0) parts.push(`${successCounts.items} item${successCounts.items === 1 ? '' : 's'}`);
    if (successCounts.classes > 0) parts.push(`${successCounts.classes} class feature${successCounts.classes === 1 ? '' : 's'}`);
    if (successCounts.feats > 0) parts.push(`${successCounts.feats} feat${successCounts.feats === 1 ? '' : 's'}`);

    if (parts.length === 0) {
      return 'No new entries imported (duplicates are skipped).';
    }

    return `Imported ${parts.join(', ')}.`;
  };

  const successMessage = formatImportSummary();

  const hasAnyImports = spellsCount > 0 || itemsCount > 0 || classesCount > 0 || featsCount > 0;

  return (
    <div className="preview-import" ref={dropdownRef}>
      <button
        type="button"
        className="preview-import-btn"
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        Import
      </button>

      {isOpen && (
        <div className="preview-import-dropdown">
          <div className="preview-import-header">
            Import from Google Sheets
          </div>

          {onImportMultiple && PREDEFINED_SOURCES.length > 0 && (
            <div className="preview-import-sources">
              <div className="preview-import-sources-header">Import from Source</div>
              <div className="preview-import-sources-list">
                {PREDEFINED_SOURCES.map((source) => (
                  <label key={source.name} className="preview-import-source-item">
                    <input
                      type="checkbox"
                      checked={selectedSources.has(source.name)}
                      onChange={() => toggleSource(source.name)}
                      disabled={isLoading}
                    />
                    <span>{source.name}</span>
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="preview-import-submit preview-import-sources-btn"
                onClick={handleImportSelected}
                disabled={isLoading || selectedSources.size === 0}
              >
                {isLoading ? 'Importing...' : `Import Selected (${selectedSources.size})`}
              </button>
            </div>
          )}

          {onImportMultiple && (
            <div className="preview-import-divider">
              <span>or paste a URL</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="preview-import-form">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste Google Sheets URL..."
              className="preview-import-input"
              disabled={isLoading}
              autoFocus
            />
            <button
              type="submit"
              className="preview-import-submit"
              disabled={isLoading || !url.trim()}
            >
              {isLoading ? 'Importing...' : 'Import'}
            </button>
          </form>

          {error && (
            <div className="preview-import-error">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="preview-import-success">
              {successMessage}
            </div>
          )}

          {warnings.length > 0 && (
            <div className="preview-import-warning">
              <details>
                <summary>Imported with warnings ({warnings.length})</summary>
                <ul>
                  {warnings.map((warning, idx) => (
                    <li key={`${warning}-${idx}`}>{warning}</li>
                  ))}
                </ul>
              </details>
            </div>
          )}

          <div className="preview-import-help">
            <div className="preview-import-templates">
              <p className="preview-import-instructions">
                Copy the combined template, add your data on the Spells, Items, Class Features, and Feats tabs, share with "Anyone with the link", then paste the URL above.
              </p>
              <div className="preview-import-template-links">
                <a
                  href={combinedTemplateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="preview-import-template"
                >
                  Template
                </a>
              </div>
            </div>
          </div>

          {hasAnyImports && (
            <div className="preview-import-clear-section">
              <div className="preview-import-clear-header">Clear Imports:</div>
              {spellsCount > 0 && (
                <button
                  type="button"
                  className="preview-import-clear-btn"
                  onClick={onClearSpells}
                  disabled={isLoading}
                >
                  Clear Spells ({spellsCount})
                </button>
              )}
              {itemsCount > 0 && (
                <button
                  type="button"
                  className="preview-import-clear-btn"
                  onClick={onClearItems}
                  disabled={isLoading}
                >
                  Clear Items ({itemsCount})
                </button>
              )}
              {classesCount > 0 && (
                <button
                  type="button"
                  className="preview-import-clear-btn"
                  onClick={onClearClasses}
                  disabled={isLoading}
                >
                  Clear Class Features ({classesCount})
                </button>
              )}
              {featsCount > 0 && onClearFeats && (
                <button
                  type="button"
                  className="preview-import-clear-btn"
                  onClick={onClearFeats}
                  disabled={isLoading}
                >
                  Clear Feats ({featsCount})
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
